import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Shield, Ban, Heart, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function RulesModal({ isOpen, onClose, onAccept }) {
  const [countdown, setCountdown] = useState(5);
  const [canAccept, setCanAccept] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCountdown(5);
      setCanAccept(false);

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCanAccept(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen]);

  const handleAccept = () => {
    if (canAccept) {
      onAccept();
    }
  };

  const handleClose = () => {
    setCountdown(5);
    setCanAccept(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[80%] max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-yellow-500" />
            Syarat dan Ketentuan Pembelian
          </DialogTitle>
          <DialogDescription>
            Harap baca dengan seksama sebelum melanjutkan
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
          {/* No Refund Rule */}
          <Alert className="border-red-500/50 bg-red-500/10">
            <Ban className="h-5 w-5 text-red-500" />
            <AlertDescription className="text-sm ml-2">
              <strong className="font-semibold">TIDAK ADA REFUND</strong>
              <br />
              Setelah pembayaran berhasil, tidak ada pengembalian dana dalam kondisi apapun. Pastikan kamu sudah yakin sebelum melanjutkan.
            </AlertDescription>
          </Alert>

          {/* Fraud Warning */}
          <Alert className="border-orange-500/50 bg-orange-500/10">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <AlertDescription className="text-sm ml-2">
              <strong className="font-semibold">ANTI-FRAUD</strong>
              <br />
              Segala bentuk fraud, chargeback, atau pembayaran palsu akan mengakibatkan banned permanen dari server tanpa pemberitahuan.
            </AlertDescription>
          </Alert>

          {/* Voluntary Transaction */}
          <Alert className="border-blue-500/50 bg-blue-500/10">
            <Heart className="h-5 w-5 text-blue-500" />
            <AlertDescription className="text-sm ml-2">
              <strong className="font-semibold">TRANSAKSI SUKARELA</strong>
              <br />
              Semua transaksi bersifat sukarela tanpa paksaan. Kamu melakukan pembelian atas kemauan sendiri dan tidak ada pihak yang memaksa.
            </AlertDescription>
          </Alert>

          {/* Additional Rules */}
          <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Ketentuan Tambahan:
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground ml-6">
              <li className="list-disc">Username yang salah menjadi tanggung jawab pembeli</li>
              <li className="list-disc">Benefit akan diberikan otomatis setelah pembayaran diterima</li>
              <li className="list-disc">Kamu WAJIB berada di dalam server saat melakukan pembayaran</li>
              <li className="list-disc">Jika ada kendala teknis, hubungi staff melalui Discord</li>
              <li className="list-disc">Semua rank dan item bersifat permanen</li>
              <li className="list-disc">Donasi tidak dapat dipindahtangankan ke akun lain</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto"
          >
            Batalkan
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!canAccept}
            className="w-full sm:w-auto"
          >
            {canAccept ? (
              'Saya Setuju & Lanjutkan'
            ) : (
              `Tunggu ${countdown} detik...`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
